import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, UserInfo } from '../services/apiService';

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  selectedSite: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setSelectedSite: (siteId: string) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const stored = localStorage.getItem('auth_credentials');
        if (stored) {
          // Try to get user info to validate credentials
          const info = await apiService.getUserInfo();
          setUserInfo(info);
          setIsAuthenticated(true);

          // Restore selected site from localStorage
          const storedSite = localStorage.getItem('selected_site');
          if (storedSite && info.sites.includes(storedSite)) {
            setSelectedSite(storedSite);
          } else if (info.sites.length > 0) {
            // Auto-select first site if available
            setSelectedSite(info.sites[0]);
            localStorage.setItem('selected_site', info.sites[0]);
          }
        }
      } catch (error) {
        // Not authenticated or credentials invalid
        apiService.clearCredentials();
        setIsAuthenticated(false);
        setUserInfo(null);
        setSelectedSite(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      // Set credentials
      apiService.setCredentials(username, password);
      
      // Get user info to validate credentials
      const info = await apiService.getUserInfo();
      setUserInfo(info);
      setIsAuthenticated(true);

      // Auto-select first site if available
      if (info.sites.length > 0) {
        setSelectedSite(info.sites[0]);
        localStorage.setItem('selected_site', info.sites[0]);
      }
    } catch (error) {
      apiService.clearCredentials();
      setIsAuthenticated(false);
      setUserInfo(null);
      setSelectedSite(null);
      throw error;
    }
  };

  const logout = () => {
    apiService.clearCredentials();
    setIsAuthenticated(false);
    setUserInfo(null);
    setSelectedSite(null);
  };

  const handleSetSelectedSite = (siteId: string) => {
    if (userInfo && userInfo.sites.includes(siteId)) {
      setSelectedSite(siteId);
      localStorage.setItem('selected_site', siteId);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userInfo,
        selectedSite,
        login,
        logout,
        setSelectedSite: handleSetSelectedSite,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};






